import { Link } from "react-router-dom";
import { Gamepad2, Compass, Users, Trophy, Award, MapPin } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";

export default function GamesHub() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="games-hub-page">
      <NavBar />

      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{T.tag}</span>
        </div>
        <h1 className="font-serif-mystery text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-4">
          🎮 {T.title}<br /><span className="italic text-[#8b6f3d]">{T.titleItalic}</span>
        </h1>
        <p className="text-lg text-zinc-600 max-w-2xl">{T.intro}</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-12">
        <div className="grid md:grid-cols-2 gap-6">
          <GameCard
            testId="game-card-nearby"
            to="/games/nearby"
            icon="🗺️"
            title={T.g1Title}
            desc={T.g1Desc}
            gradient="from-emerald-500/20 via-teal-400/20 to-blue-500/20"
            btn={T.g1Btn}
          />
          <GameCard
            testId="game-card-alien-zombies"
            to="/games/alien-zombies"
            icon="👽🧟"
            title={T.g2Title}
            desc={T.g2Desc}
            gradient="from-purple-500/20 via-pink-500/20 to-orange-500/20"
            btn={T.g2Btn}
          />
        </div>
      </section>

      {/* Quick links */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <h2 className="font-serif-mystery text-2xl mb-4">{T.quickLinks}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickLink to="/games/profile" icon={<Award size={16} />} label={T.myProfile} testId="qlink-profile" />
          <QuickLink to="/games/friends" icon={<Users size={16} />} label={T.myFriends} testId="qlink-friends" />
          <QuickLink to="/games/teams" icon={<Trophy size={16} />} label={T.myTeams} testId="qlink-teams" />
          <QuickLink to="/games/leaderboard" icon={<Trophy size={16} />} label={T.leaderboards} testId="qlink-leaderboards" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function GameCard({ to, icon, title, desc, gradient, btn, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className={`group relative overflow-hidden rounded-3xl p-8 border border-zinc-200 hover:border-[#8b6f3d] hover:shadow-2xl transition-all bg-gradient-to-br ${gradient}`}>
      <div className="text-7xl mb-4 group-hover:scale-110 transition-transform">{icon}</div>
      <h3 className="font-serif-mystery text-3xl mb-3">{title}</h3>
      <p className="text-zinc-700 leading-relaxed mb-6">{desc}</p>
      <div className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-zinc-900 text-white group-hover:bg-[#8b6f3d] transition-colors">
        <Gamepad2 size={16} /> {btn}
      </div>
    </Link>
  );
}

function QuickLink({ to, icon, label, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="flex items-center gap-2 border border-zinc-200 rounded-xl px-4 py-3 hover:border-[#8b6f3d] hover:bg-amber-50/40 transition-colors">
      <span className="text-[#8b6f3d]">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

const FR = {
  tag: "Jeux",
  title: "Jouez, explorez,",
  titleItalic: "gagnez des points",
  intro: "Deux jeux, un objectif : rendre l'exploration des mystères du monde encore plus amusante. Nouveaux jeux à venir !",
  g1Title: "Les 5 mystères les plus proches",
  g1Desc: "Localise les 5 lieux mystérieux les plus proches de toi. Pars sur le terrain, complète des missions, gagne 50 points par mission.",
  g1Btn: "🧭 Jouer",
  g2Title: "Alien & Zombies",
  g2Desc: "Réalité augmentée avec ta caméra. Repère, vise et élimine les zombies et aliens qui apparaissent autour de toi. Vagues, combos, vies, podium mondial.",
  g2Btn: "👽🧟 Jouer",
  quickLinks: "Accès rapide",
  myProfile: "Mon profil",
  myFriends: "Mes amis",
  myTeams: "Mes équipes",
  leaderboards: "Classements",
};
const EN = {
  tag: "Games",
  title: "Play, explore,",
  titleItalic: "earn points",
  intro: "Two games, one goal: making world mystery exploration even more fun. More games coming!",
  g1Title: "5 Closest Mysteries",
  g1Desc: "Locate the 5 mysterious places nearest to you. Head out, complete missions, earn 50 points per mission.",
  g1Btn: "🧭 Play",
  g2Title: "Alien & Zombies",
  g2Desc: "Augmented reality with your camera. Spot, aim and take down zombies and aliens appearing around you. Waves, combos, lives, worldwide leaderboard.",
  g2Btn: "👽🧟 Play",
  quickLinks: "Quick access",
  myProfile: "My profile",
  myFriends: "My friends",
  myTeams: "My teams",
  leaderboards: "Leaderboards",
};
